import {usePlantRunningTimes} from '@domain/running-times'
import {usePlantConfig} from '@domain/shifthandover'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import type {
  TimeZone,
  EquipmentRunningTimesDto as EquipmentRunningTimes,
  GetStackedBarChartComponent,
  Iso8601
} from '@hconnect/common/types'
import {useUpmPlant} from '@hconnect/upm'
import {useHasPermission} from '@hproduce/permissions'
import {Typography} from '@mui/material'
import {
  OperationHoursLegendTooltip,
  mapEquipmentRunningTimes,
  EquipmentRunningTimesResult,
  getEquipmentStoppageCodesByStoppageType
} from '@widgets/running-times'
import {get, isEmpty, last} from 'lodash'
import {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'

type PlantRunningTimesProps = {
  plantId: string
  from: Iso8601
  to: Iso8601
  timezone: TimeZone
  getStackedBarChartComponent: GetStackedBarChartComponent
  onEquipmentClick: (equipmentId: string) => void
}

export const PlantRunningTimes: FC<PlantRunningTimesProps> = ({
  plantId,
  from,
  to,
  timezone,
  getStackedBarChartComponent,
  onEquipmentClick
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const upmPlant = useUpmPlant({plantId})
  const hasPermission = useHasPermission({plantId, countryId: upmPlant.country})

  const {data: plantConfig} = usePlantConfig({plantId, language})
  const stoppageCodes = useMemo(
    () => getEquipmentStoppageCodesByStoppageType(plantConfig),
    [plantConfig]
  )

  const {
    data: runningTimePerformances,
    isLoading: isRunningTimePerformanceLoading,
    error: runningTimePerformancesError,
    refetch: refetchRunningTimePerformance
  } = usePlantRunningTimes({plantId, from, to})

  const errorMessage = runningTimePerformancesError
    ? t('runningTimes.message.fetchingError')
    : undefined

  return (
    <ContentBox
      mode="max100PercentOfParentHeight"
      title={
        <Typography component="span" variant="h3" display="flex" alignItems="center">
          <>
            {t('runningTimes.cardTitle')}
            {!isEmpty(runningTimePerformances) && <OperationHoursLegendTooltip />}
          </>
        </Typography>
      }
      isLoading={isRunningTimePerformanceLoading}
      bodyWithPadding
      errorMessage={errorMessage}
      minHeight="150px"
      data-test-id="plant-running-times"
    >
      {runningTimePerformances?.map(
        (equipmentPerformanceData: EquipmentRunningTimes, index: number) => {
          const activeStatus = get(last(equipmentPerformanceData.runningTimes), 'runningTimeType')
          return (
            <EquipmentRunningTimesResult
              runningTimePerformance={
                mapEquipmentRunningTimes([equipmentPerformanceData], timezone)[0]
              }
              equipment={equipmentPerformanceData.equipment}
              key={equipmentPerformanceData.equipment.id}
              getStackedBarChartComponent={getStackedBarChartComponent}
              stoppageCodes={{
                kilnStoppageCodes: stoppageCodes.kiln,
                rawMillStoppageCodes: stoppageCodes.rawMill,
                finishMillStoppageCodes: stoppageCodes.cementMill,
                alternativeFuelInstallationStoppageCodes: stoppageCodes.alternativeFuelInstallation
              }}
              hideDivider={runningTimePerformances?.length === index + 1}
              activeStatus={activeStatus}
              plantId={plantId}
              onClick={() => onEquipmentClick(equipmentPerformanceData.equipment.id)}
              onStoppageCreationSuccess={refetchRunningTimePerformance}
              hasPermission={hasPermission}
              plantTimeZone={timezone}
            />
          )
        }
      )}
    </ContentBox>
  )
}
